/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { Button, Col } from 'react-bootstrap';
import {
  Form,
  Input,
  InputNumber,
  Tooltip,
  Checkbox,
  Modal,
  Button as AntButton,
  Divider,
  Alert,
} from 'antd';
import ReactDataSheet from 'react-datasheet';
import { toast } from 'react-toastify';

import { SPDxPlot } from 'components/plots';
import ManufactureForm from 'components/metaDataForm';
import EditAlphaOpicData from 'components/tabs/luox/editAlphaOpicData';
import { LoadingScreen } from 'components/loadingScreen';
import { getLuoxData, convertLuoxData } from 'utils';

import {
  setReportData,
  setLibraryData,
  setPasteLuoxData,
  setLuoxUnits,
  setShouldCalculateLuoxData,
} from 'redux/actions';
import '../index.scss';

const Paste = ({ view }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const pasteLuoxData = useSelector(
    (state) => state.dataReducers.pasteLuoxData
  );
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);

  const initialGrid = [
    [
      { readOnly: true, value: 'Wavelength (nm)' },
      {
        readOnly: true,
        className: 'cell',
        value: 'Power',
      },
    ],
  ];

  const [spdValues, setSpdValues] = useState([]);
  const [spdWavelengths, setSpdWavelengths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wavelengthStart, setWavelengthStart] = useState({
    validateStatus: 'success',
    value: 380,
  });
  const [wavelengthEnd, setWavelengthEnd] = useState({
    validateStatus: 'success',
    value: 780,
  });
  const [step, setStep] = useState({
    validateStatus: 'success',
    value: 1,
  });
  const [disabled, setDisabled] = useState(false);
  const [tableReady, setTableReady] = useState(false);
  const [grid, setGrid] = useState(initialGrid);
  const [metadata, setMetadata] = useState({
    manufacturer: '',
    model: '',
    description: '',
    creator: '',
    identifier: '',
    measurement: '',
    laboratory: '',
    report_number: '',
    report_date: '',
    creation_date: '',
    comments: '',
    spectral_quantity: '',
  });
  const [showLuoxModal, setShowLuoxModal] = useState(false);
  const [shouldCalculateLuoxData, setShouldCalculateLuoxData] = useState(false);
  const [pasteDataWarnings, setPasteDataWarnings] = useState([]);
  const [showExcelAlert, setShowExcelAlert] = useState(true);

  useEffect(() => {
    if (shouldCalculateLuoxData) {
      const luoxData = getLuoxData(spdWavelengths, spdValues);
      dispatch(setPasteLuoxData(luoxData));
    }
    // else {
    //   dispatch(setPasteLuoxData({}));
    // }
  }, [spdWavelengths, spdValues, shouldCalculateLuoxData]);

  const handleLuoxCalculation = (e) => {
    const luox = e.target.checked;
    setShouldCalculateLuoxData(luox);
    if (luox) {
      const luoxData = getLuoxData(spdWavelengths, spdValues);
      dispatch(setPasteLuoxData(luoxData));
    } else {
      dispatch(setPasteLuoxData({}));
    }
  };

  const populateSPDCells = () => {
    let gridItems = [];
    let wavelengths = [];
    let spd_length = wavelengthEnd.value - wavelengthStart.value + 1;

    for (let i = 0; i < spd_length; i += parseInt(step.value)) {
      const newWavelength = i + parseInt(wavelengthStart.value);

      if (newWavelength >= 380 && newWavelength <= 780) {
        wavelengths.push(i + parseInt(wavelengthStart.value));
      }
      gridItems.push([
        {
          readOnly: true,
          value: i + parseInt(wavelengthStart.value),
          className: 'cell',
        },
        { value: '' },
      ]);
    }
    setGrid([
      [
        { readOnly: true, value: 'Wavelength (nm)' },
        {
          readOnly: true,
          className: 'cell',
          value: 'Power',
        },
      ],
      ...gridItems,
    ]);
    setSpdWavelengths(wavelengths);
  };

  const handleSubmitTable = (submitType) => {
    if (spdValues.length !== 0 && !spdValues.includes('')) {
      setLoading(true);
      axios
        .post(`${window.location.origin}/api/calculate`, {
          // .post(`http://localhost:5000/api/calculate`, {
          // .post(`https://tm30-final-server.herokuapp.com/api/calculate`, {
          // .post(`https://spectralcalculator.pnl.gov/api/calculate`, {
          spd: spdValues,
          wavelength: spdWavelengths,
        })
        .then((res) => {
          return res.data;
        })
        .then((data) => {
          setLoading(false);
          setTableReady(true);

          if (!data.error) {
            let tm30Data = data.data.tm30;
            let numericalResults = tm30Data.Numerical_Results;
            const duvTest = numericalResults.DUV[0].toFixed(4);
            const cctTest = numericalResults.CCT[0].toFixed(4);
            if (cctTest > 19900) {
              toast.error(
                <div>
                  <h3>Error</h3>{' '}
                  <p>
                    The CCT is greater than this program was designed to
                    calculate. TM-30 calculations were not completed.
                  </p>
                </div>,
                {
                  toastId: 'paste-toast-fail-1',
                }
              );
            } else if (duvTest > 0.04 || duvTest < -0.04) {
              toast.error(
                <div>
                  <h3>Error</h3>{' '}
                  <p>
                    The chromaticity of the SPD is too far off the Planckian
                    locus. TM-30 calculations were not completed.
                  </p>
                </div>,
                {
                  toastId: 'paste-toast-fail-2',
                }
              );
            } else {
              tm30Data = { ...tm30Data, Numerical_Results: numericalResults };
              let reportData = {
                id: 1,
                tm30: tm30Data,
                metadata: metadata,
              };
              if (shouldCalculateLuoxData) {
                Object.assign(reportData, { luoxData: pasteLuoxData });
              }
              let message = null;
              if (
                duvTest > 0.0091 ||
                duvTest < -0.018 ||
                cctTest < 2136 ||
                cctTest > 7042
              ) {
                message =
                  'The chromaticity is outside the range specified in ANSI C78.377-2017. Interpret results with caution.';
                alert(message);
              }
              const newPasteDataWarnings = message
                ? [...pasteDataWarnings, message]
                : pasteDataWarnings;
              Object.assign(reportData, {
                warnings: newPasteDataWarnings,
              });
              if (submitType === 'calculate') {
                dispatch(setReportData(reportData));
                history.push({
                  pathname: '/results',
                });
              } else {
                // Standardizing area scale and power scales
                if (shouldCalculateLuoxData) {
                  const libraryStandardLuoxUnits = {
                    ...luoxUnits,
                    ...{ areaScale: 'm2', powerScale: 'mW' },
                  };
                  const modifiedLuoxData = convertLuoxData(
                    pasteLuoxData.luoxInput,
                    libraryStandardLuoxUnits
                  );
                  Object.assign(reportData, {
                    luoxData: {
                      ...modifiedLuoxData,
                      luoxUnits: libraryStandardLuoxUnits,
                    },
                  });
                }
                dispatch(setLibraryData([reportData]));
                history.push({
                  pathname: '/user-library',
                });
              }
            }
          } else {
            toast.error(
              <div>
                <h3>Error</h3> <p>{data.error}</p>
              </div>,
              {
                toastId: 'paste-toast-fail',
              }
            );
          }
        })
        .catch((error) => {
          alert('Oops...Something went wrong!');
          setLoading(false);
        });
    } else {
      alert(
        'Error: Length of SPD array does not match length of wavelength array.'
      );
    }
  };

  const handleCellChanges = (changes) => {
    // let updatedSpdValues = [];
    const updatedGrid = grid.map((row) => [...row]);
    changes.forEach(({ row, col, value }) => {
      const intValue = value == 0 ? '0.000000' : parseFloat(value).toFixed(6);
      // updatedSpdValues = [...updatedSpdValues, intValue];
      updatedGrid[row][col] = {
        ...updatedGrid[row][col],
        value: intValue,
      };
    });
    let updatedSpdValues = updatedGrid.map((grid) => grid[1].value);
    updatedSpdValues.shift();
    let updatedSpdWavelengths = spdWavelengths;
    // Chopping of extra spd values below 380 and above 780 wavelengths
    if (wavelengthStart.value < 380 || wavelengthEnd.value > 780) {
      let message =
        'The required wavelength range is 380 - 780 nm. Data outside this range has not been used in the calculation.';
      setPasteDataWarnings([...pasteDataWarnings, message]);
      alert(message);
    }
    if (wavelengthStart.value < 380) {
      const spdsToRemoveFromStart = Math.round(
        (380 - wavelengthStart.value) / step.value
      );
      updatedSpdValues.splice(0, spdsToRemoveFromStart);
    }
    if (wavelengthEnd.value > 780) {
      const spdsToRemoveFromEnd = Math.round(
        (wavelengthEnd.value - 780) / step.value
      );
      updatedSpdValues.splice(updatedSpdValues.length - spdsToRemoveFromEnd);
    }

    // Adding zeros if there is missing data
    if (wavelengthStart.value > 380 || wavelengthEnd.value < 780) {
      let message =
        'The required wavelength range is 380 - 780 nm. Missing data has been replaced by zeros in the calculation.';
      setPasteDataWarnings([...pasteDataWarnings, message]);
      alert(message);
    }

    if (wavelengthStart.value > 380) {
      let newSpdWavelengthsToAdd = [];
      for (let i = 380; i < wavelengthStart.value; i += parseInt(step.value)) {
        newSpdWavelengthsToAdd.push(i);
      }
      updatedSpdWavelengths = [
        ...newSpdWavelengthsToAdd,
        ...updatedSpdWavelengths,
      ];
      const addZerosToSpdValues = newSpdWavelengthsToAdd.map(
        (item) => '0.000000'
      );
      updatedSpdValues = [...addZerosToSpdValues, ...updatedSpdValues];
    }

    if (wavelengthEnd.value < 780) {
      let newSpdWavelengthsToAdd = [];
      for (
        let i = parseInt(wavelengthEnd.value) + parseInt(step.value);
        i <= 780;
        i += parseInt(step.value)
      ) {
        newSpdWavelengthsToAdd.push(i);
      }
      updatedSpdWavelengths = [
        ...updatedSpdWavelengths,
        ...newSpdWavelengthsToAdd,
      ];
      const addZerosToSpdValues = newSpdWavelengthsToAdd.map(
        (item) => '0.000000'
      );
      updatedSpdValues = [...updatedSpdValues, ...addZerosToSpdValues];
    }
    setSpdValues(updatedSpdValues);
    setSpdWavelengths(updatedSpdWavelengths);
    setGrid(updatedGrid);
  };

  const layout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const handleCreateTable = (values) => {
    setTableReady(true);
    setGrid(initialGrid);
    setSpdValues([]);
    setSpdWavelengths([]);
    populateSPDCells();
  };

  const handleStartWavelengthChange = (event) => {
    const value = event.target.value;
    let validationState;
    if (value < 380) {
      validationState = {
        validationStatus: 'warning',
        message: (
          <strong>
            Data for wavelengths shorter than 380 nm will not be used in the
            calculation.
          </strong>
        ),
      };
    } else if (value <= 400) {
      validationState = {
        validationStatus: 'success',
        message: null,
      };
    } else {
      validationState = {
        validationStatus: 'error',
        message: 'Start wavelength value cannot exceed 400',
      };
    }
    setWavelengthStart({ ...validationState, value });
  };

  const handleEndWavelengthChange = (event) => {
    const value = event.target.value;
    let validationState;
    if (value > 780) {
      validationState = {
        validationStatus: 'warning',
        message: (
          <strong>
            Data for wavelengths longer than 780 nm will not be used in the
            calculation.
          </strong>
        ),
      };
    } else if (value >= 700) {
      validationState = {
        validationStatus: 'success',
        message: null,
      };
    } else {
      validationState = {
        validationStatus: 'error',
        message: 'End wavelength value cannot be less than 700',
      };
    }
    setWavelengthEnd({ ...validationState, value });
  };

  const handleStepChange = (value) => {
    let validationState;
    if (value > 5) {
      validationState = {
        validationStatus: 'error',
        message: (
          <strong>Cannot perform calculations above step size 5.</strong>
        ),
      };
    }
    setStep({ ...validationState, value });
  };

  return (
    <Fragment>
      <Col md={8}>
        <Form
          className='paste-data-form'
          {...layout}
          onFinish={handleCreateTable}
          initialValues={{ start: 380, end: 780, interval: 1 }}
        >
          <Form.Item
            hasFeedback
            tooltip='Data beyond the extents of 380 to 780 nm will be discarded. The minimum
            range for calculation is 400 to 700 nm.'
            label='Start Wavelength (nm)'
            name='start'
            rules={[
              {
                required: true,
                message: 'Please input start wavelength',
              },
            ]}
            validateStatus={wavelengthStart.validationStatus}
            help={wavelengthStart.errorMsg}
          >
            <Tooltip
              trigger={
                ['error', 'warning'].includes(wavelengthStart.validationStatus)
                  ? 'hover'
                  : []
              }
              placement='right'
              title={wavelengthStart.message}
            >
              <Input
                style={{ width: 280 }}
                value={wavelengthStart.value}
                onChange={handleStartWavelengthChange}
              />
            </Tooltip>
          </Form.Item>
          <Form.Item
            hasFeedback
            tooltip='Data beyond the extents of 380 to 780 nm will be discarded. The minimum
          range for calculation is 400 to 700 nm.'
            label='End Wavelength (nm)'
            name='end'
            rules={[
              {
                required: true,
                message: 'Please input end wavelength',
              },
            ]}
            validateStatus={wavelengthEnd.validationStatus}
            help={wavelengthEnd.errorMsg}
          >
            <Tooltip
              trigger={
                ['error', 'warning'].includes(wavelengthEnd.validationStatus)
                  ? 'hover'
                  : []
              }
              placement='right'
              title={wavelengthEnd.message}
            >
              <Input
                style={{ width: 280 }}
                value={wavelengthEnd.value}
                onChange={handleEndWavelengthChange}
              />
            </Tooltip>
          </Form.Item>
          <Form.Item
            hasFeedback
            tooltip='The interval must be an integer between 1 and 5'
            label='Interval (nm)'
            name='interval'
            rules={[
              {
                required: true,
                message: 'Please input interval',
              },
            ]}
            validateStatus={step.validationStatus}
          >
            <Tooltip
              trigger={
                ['error', 'warning'].includes(step.validationStatus)
                  ? 'hover'
                  : []
              }
              placement='right'
              title={step.message}
            >
              <InputNumber
                style={{ width: 280 }}
                min={1}
                max={10}
                value={step.value}
                onChange={handleStepChange}
              />
            </Tooltip>
          </Form.Item>
          <Form.Item className='submit-button'>
            <Button
              bsStyle='primary'
              type='submit'
              disabled={
                wavelengthStart.validationStatus === 'error' ||
                wavelengthEnd.validationStatus === 'error' ||
                step.validationStatus === 'error'
              }
            >
              Create Table
            </Button>
          </Form.Item>
        </Form>
      </Col>
      {/* <Col md={2} css={ActiveColumnArrowStyle}>
        {view === "Paste Data" && !tableReady && (
          <Icon icon="arrow-right" iconDisplay="solid" />
        )}
      </Col> */}
      {view === 'paste' ? (
        <Col css={OutputColumnStyle(tableReady)} md={6}>
          <Modal
            title='Edit Alpha-Opic Data'
            visible={showLuoxModal}
            onCancel={() => setShowLuoxModal(false)}
            footer={[
              <AntButton key='clear' onClick={() => setShowLuoxModal(false)}>
                Close
              </AntButton>,
            ]}
          >
            <EditAlphaOpicData measurementName={metadata.identifier} />
          </Modal>
          <div>
            <Col xs={6} className='metadata-form'>
              <div>
                <strong>Calculations to Perform:</strong>
              </div>
              <br />
              <Checkbox checked>IES TM-30 Color Rendition</Checkbox>
              <br />
              <Checkbox onChange={handleLuoxCalculation}>
                CIE S026 Alpha-Opic{' '}
              </Checkbox>
              <Divider css={dividerStyle} />
              <div css={luoxDataContainerStyle(shouldCalculateLuoxData)}>
                <EditAlphaOpicData measurementName={metadata.identifier} />
              </div>
              <ManufactureForm metadata={metadata} setMetadata={setMetadata} />
            </Col>
            <Col xs={6}>
              <div>
                <SPDxPlot
                  inputType='paste'
                  spd={spdValues}
                  wavelength={spdWavelengths}
                />
              </div>
              <br />
              {showExcelAlert && (
                <Alert
                  message='When pasting from Excel, only visible data will copy, not
              the full precision value. Increase the number of visible decimal
              places in Excel to ensure accurate calculations.'
                  type='info'
                  showIcon
                  closable
                  onClose={() => setShowExcelAlert(!showExcelAlert)}
                />
              )}
              <div
                className={`data-table paste-table ${
                  showExcelAlert && 'alert'
                }`}
              >
                <ReactDataSheet
                  data={grid}
                  valueRenderer={(cell) => cell.value}
                  onCellsChanged={(changes) => handleCellChanges(changes)}
                />
              </div>
              <div css={SpinnerStyle}>
                <LoadingScreen show={loading} />
              </div>
              <br />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-evenly',
                }}
              >
                <Button
                  style={{ marginRight: '1rem' }}
                  type='submit'
                  bsStyle='primary'
                  onClick={() => handleSubmitTable('calculate')}
                  disabled={disabled}
                >
                  Calculate
                </Button>
                <Button
                  bsStyle='success'
                  onClick={() => handleSubmitTable('AddToLibrary')}
                  disabled={disabled}
                >
                  Add to User Library
                </Button>
              </div>
            </Col>
          </div>
        </Col>
      ) : null}
      {!tableReady && view === 'paste' && (
        <p css={EmptyOutputTextStyle}>
          Use one of the three options to the left (Call from Library, Import
          SPDX, or Paste Data) to initiate a calculation.
        </p>
      )}
    </Fragment>
  );
};

export default Paste;

Paste.propTypes = {
  view: PropTypes.string,
  metaData: PropTypes.object,
};

const OutputColumnStyle = (exampleOpen) => css`
  position: absolute;
  left: 47rem;
  top: -1rem;
  width: 68rem;
  height: 74rem;
  background: white;
  border-radius: 2rem;
  padding: 20px;
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.2);
  border: 1px solid #d9d9d9;

  ${!exampleOpen
    ? css`
        opacity: 0.2;
        pointer-events: none;
      `
    : ''}
`;

const EmptyOutputTextStyle = css`
  position: absolute;
  left: 56rem;
  top: 18.8rem;
  width: 46rem;
  font-size: 20px;
  font-weight: bold;
`;

const SpinnerStyle = css`
  position: absolute;
  right: 12rem;
  top: 0;
  width: 40rem;
`;

const luoxDataContainerStyle = (shouldCalculateLuoxData) => css`
  opacity: ${shouldCalculateLuoxData ? '1' : '0'};
  pointer-events: ${shouldCalculateLuoxData ? 'auto' : 'none'};
  height: ${shouldCalculateLuoxData ? '30rem' : '1rem'};

  transition: all 400ms;
`;

const dividerStyle = css`
  margin: 12px;
`;
